import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button as MuiButton,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,Button
} from "@mui/material";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import ProjectViewController from "../../redux/actions/ProjectApiAction";
import * as Yup from "yup";
import EmployeeProfileVeiwController from "../../redux/actions/EmployeeProfileApiAction";
import { FadeLoader } from "react-spinners";
import EmployerViewController from "../../redux/actions/EmployerApiAction";
import { IMAGE_URL } from "../../services/api";
import ProtectedImage from "../../components/ProtectedImage";

function ProjectFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addProjects, updateProject } = ProjectViewController();
  const { getEmployees } = EmployeeProfileVeiwController();
  const { getAllEmployeeEmployer } = EmployerViewController();
  const { employers } = useSelector((state) => state.employer);
  const { employee_data } = useSelector((state) => state.employeeProfile);
  const employees = employee_data?.employees || [];
  const isEdit = Boolean(initialData?.id);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const { isLoding } = useSelector((state) => state.project);

  const [initialValues, setInitialValues] = useState({
    title: "",
    description: "",
    project_leader: "",
    scope: "",
    start_date: "",
    end_date: "",
    project_budget: "",
    is_active: false,
    attachments: [],
  });

  useEffect(() => {
    if (show && (!employers || employers.length === 0)) {
      dispatch(getAllEmployeeEmployer());
    }

    if (show && initialData) {
      setInitialValues({
        title: initialData.title || "",
        description: initialData.description || "",
        scope: initialData.scope || "",
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        project_budget: initialData.project_budget || "",
        project_leader: initialData.project_leader.id || "",
        is_active: initialData.is_active ?? false,
        attachments: [],
      });
      setExistingAttachments(initialData.attachments || []);
    } else if (show && !initialData) {
      setInitialValues({
        title: "",
        description: "",
        scope: "",
        start_date: "",
        end_date: "",
        project_budget: "",
        project_leader: "",
        is_active: false,
        attachments: [],
      });
      setExistingAttachments([]);
    }
  }, [show, initialData]);

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    description: Yup.string().required("Description is required"),
    scope: Yup.string().required("Project scope is required"),
    start_date: Yup.date().required("Start Date is required"),
    end_date: Yup.date().required("End Date is required"),
  });

  const handleSubmit = (values, { resetForm, setSubmitting }) => {
    const formData = new FormData();
    for (const key in values) {
      if (key !== "attachments") {
        formData.append(key, values[key]);
      }
    }
    if (values.attachments && values.attachments.length > 0) {
      Array.from(values.attachments).forEach((file) => {
        formData.append("attachments", file);
      });
    }
    const action = isEdit
      ? updateProject(initialData.id, formData)
      : addProjects(formData);

    dispatch(action)
      .then(() => {
        resetForm();
        handleClose();
      })
      .catch((err) => {
        console.error("Submission Error:", err);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Dialog open={show} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{isEdit ? "Edit Project" : "Add Project"}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ setFieldValue, errors, touched, values, handleChange, isSubmitting }) => (
          <Form>
            <DialogContent dividers style={{ maxHeight: "65vh", overflowY: "auto" }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    name="start_date"
                    InputLabelProps={{ shrink: true }}
                    value={values.start_date}
                    onChange={handleChange}
                    error={touched.start_date && Boolean(errors.start_date)}
                    helperText={touched.start_date && errors.start_date}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    name="end_date"
                    InputLabelProps={{ shrink: true }}
                    value={values.end_date}
                    onChange={handleChange}
                    error={touched.end_date && Boolean(errors.end_date)}
                    helperText={touched.end_date && errors.end_date}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Scope"
                    name="scope"
                    value={values.scope}
                    onChange={handleChange}
                    error={touched.scope && Boolean(errors.scope)}
                    helperText={touched.scope && errors.scope}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Budget"
                    name="project_budget"
                    value={values.project_budget}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={employers}
                    getOptionLabel={(emp) => `${emp.first_name} ${emp.last_name}`}
                    value={
                      employers.find((e) => e.id === values.project_leader) || null
                    }
                    onChange={(event, newValue) =>
                      setFieldValue("project_leader", newValue ? newValue.id : "")
                    }
                    renderInput={(params) => <TextField {...params} label="Project Leader" />}
                  />
                </Grid>

                {isEdit && existingAttachments.length > 0 && (
                  <Grid item xs={12}>
                    <label>Existing Attachments:</label>
                    {existingAttachments.map((url, index) => {
                      const fullUrl = `${IMAGE_URL}${url}`;
                      const isPDF = fullUrl.toLowerCase().endsWith(".pdf");
                      const isImage = /\.(jpg|jpeg|png|gif)$/i.test(fullUrl);
                      return (
                        <div key={index}>
                          {isImage ? (
                            // <img
                            //   src={fullUrl}
                            //   alt={`Attachment ${index + 1}`}
                            //   style={{
                            //     width: "100px",
                            //     height: "100px",
                            //     objectFit: "cover",
                            //     marginRight: "10px",
                            //     borderRadius: "8px",
                            //     cursor: "pointer",
                            //   }}
                            //   onClick={() => window.open(fullUrl, "_blank")}
                            // />
                            <ProtectedImage
  path={fullUrl}
  alt={`Attachment ${index + 1}`}
  style={{
    width: "100px",
    height: "100px",
    objectFit: "cover",
    marginRight: "10px",
    borderRadius: "8px",
    cursor: "pointer",
  }}
  onClick={() => window.open(fullUrl, "_blank")}
/>

                          ) : isPDF ? (
                            <p
                              style={{
                                color: "#007bff",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              onClick={() => window.open(fullUrl, "_blank")}
                            >
                              View PDF {index + 1}
                            </p>
                          ) : (
                            <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                              Download File {index + 1}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </Grid>
                )}

                <Grid item xs={12}>
                <Typography gutterBottom>
                  Attachments
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  size="small"
                >
                  Upload Files
                  <input
                    name="attachments"
                    type="file"
                    multiple
                    hidden
                    onChange={(event) => {
                      setFieldValue("attachments", event.currentTarget.files);
                    }}
                  />
                </Button>

                {values.attachments && values.attachments.length > 0 && (
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {Array.from(values.attachments).map((file, index) => (
                      <li key={index} style={{ fontSize: "0.875rem" }}>
                        {file.name}
                      </li>
                    ))}
                  </ul>
                )}
              </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="is_active"
                        checked={values.is_active}
                        onChange={handleChange}
                      />
                    }
                    label="Is Active"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions>
              <MuiButton variant="outlined" onClick={handleClose}>
                Cancel
              </MuiButton>
              <MuiButton type="submit" variant="contained" disabled={isSubmitting}>
                {isEdit ? "Update" : "Submit"}
              </MuiButton>
              {isLoding && (
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    height: "100vh",
                    width: "100vw",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "rgba(255, 255, 255, 0.6)",
                    zIndex: 9999,
                  }}
                >
                  <div
                    style={{
                      background:
                        "linear-gradient(45deg,rgb(65, 63, 63),rgb(238, 231, 238))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    <FadeLoader loading={true} size={150} aria-label="Loading Spinner" />
                  </div>
                </div>
              )}
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

export default ProjectFormModal;
